import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { localApi, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  CreditCard, CheckCircle, Warning, ClockCountdown, Prohibit,
  CircleNotch, Receipt, Ticket, Download,
} from "@phosphor-icons/react";

const inputCls =
  "rounded-sm border-input bg-background text-foreground placeholder:text-muted-foreground focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-0";
const btnPrimary = "rounded-sm bg-primary text-primary-foreground hover:bg-primary/80";

const SUB_STATUS = {
  activa: { label: "Activa", cls: "border-emerald-500/40 text-emerald-500", icon: CheckCircle },
  por_vencer: { label: "Por vencer", cls: "border-amber-500/40 text-amber-500", icon: ClockCountdown },
  vencida: { label: "Vencida", cls: "border-red-500/40 text-red-400", icon: Warning },
  suspendida: { label: "Suspendida", cls: "border-red-500/40 text-red-400", icon: Prohibit },
};

const FEATURE_LABELS = {
  empleados: "Empleados", turnos: "Turnos", horarios: "Horarios", asistencia: "Asistencia",
  planillas: "Planillas", solicitudes: "Solicitudes", documentos: "Documentos",
  notificaciones: "Notificaciones", reportes: "Reportes", vouchers_email: "Vouchers por correo",
  adjuntos: "Adjuntos", marca_personalizada: "Marca personalizada",
};

const fmtMoney = (n, c = "HNL") => `${c === "HNL" ? "L" : c} ${Number(n || 0).toLocaleString("es-HN", { minimumFractionDigits: 2 })}`;
const fmtDate = (d) => {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString("es-HN", { day: "numeric", month: "short", year: "numeric" }); } catch { return "—"; }
};

function PayPalButtons({ clientId, planId, cycle, code, onPaid }) {
  const ref = useRef(null);
  const [ready, setReady] = useState(false);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    const scriptId = "paypal-sdk-script";
    const existing = document.getElementById(scriptId);
    if (existing && window.paypal) { setReady(true); return; }
    if (!existing) {
      const s = document.createElement("script");
      s.id = scriptId;
      s.src = `https://www.paypal.com/sdk/js?client-id=${clientId}&currency=USD&intent=capture`;
      s.onload = () => setReady(true);
      s.onerror = () => toast.error("No se pudo cargar PayPal");
      document.body.appendChild(s);
    } else {
      existing.addEventListener("load", () => setReady(true));
    }
  }, [clientId]);

  useEffect(() => {
    if (!ready || !ref.current) return;
    ref.current.innerHTML = "";
    window.paypal.Buttons({
      style: { layout: "vertical", shape: "rect", label: "pay" },
      createOrder: async () => {
        const { data } = await localApi.post("/billing/paypal/create-order", { plan_id: planId, cycle, code });
        return data.order_id;
      },
      onApprove: async (d) => {
        setProcessing(true);
        try {
          await localApi.post("/billing/paypal/capture", { order_id: d.orderID });
          toast.success("Pago confirmado. ¡Tu suscripción está activa!");
          onPaid();
        } catch (e) {
          toast.error(apiError(e, "No se pudo confirmar el pago"));
        } finally {
          setProcessing(false);
        }
      },
      onError: () => toast.error("Error en PayPal. Intenta de nuevo."),
    }).render(ref.current).catch(() => {});
  }, [ready, planId, cycle, code]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div data-testid="paypal-buttons-wrap">
      {processing && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
          <CircleNotch className="animate-spin" size={15} /> Confirmando pago con PayPal...
        </div>
      )}
      {!ready && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-3">
          <CircleNotch className="animate-spin" size={15} /> Cargando PayPal...
        </div>
      )}
      <div ref={ref} data-testid="paypal-buttons" />
    </div>
  );
}

export default function MiSuscripcion() {
  const [data, setData] = useState(null);
  const [plans, setPlans] = useState([]);
  const [paypalCfg, setPaypalCfg] = useState({ enabled: false });
  const [cycle, setCycle] = useState("monthly");
  const [checkout, setCheckout] = useState(null);
  const [quote, setQuote] = useState(null);
  const [code, setCode] = useState("");
  const [quoting, setQuoting] = useState(false);
  const [activatingFree, setActivatingFree] = useState(false);

  const load = useCallback(async () => {
    try {
      const [sub, pl, pp] = await Promise.all([
        localApi.get("/billing/my-subscription"),
        localApi.get("/billing/plans"),
        localApi.get("/billing/paypal-config"),
      ]);
      setData(sub.data);
      setPlans(pl.data || []);
      setPaypalCfg(pp.data || { enabled: false });
    } catch (e) {
      toast.error(apiError(e, "No se pudo cargar tu suscripción"));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openCheckout = async (plan) => {
    setCheckout(plan);
    setQuote(null);
    setCode("");
    if (plan.is_free) return;
    setQuoting(true);
    try {
      const { data: q } = await localApi.post("/billing/validate-discount", { plan_id: plan.id, cycle });
      setQuote(q);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setQuoting(false);
    }
  };

  const applyCode = async () => {
    if (!checkout) return;
    setQuoting(true);
    try {
      const { data: q } = await localApi.post("/billing/validate-discount", { plan_id: checkout.id, cycle, code });
      setQuote(q);
      if (q.discount > 0) toast.success(`Descuento aplicado: ${q.discount_name || code}`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setQuoting(false);
    }
  };

  const subscribeFree = async () => {
    setActivatingFree(true);
    try {
      await localApi.post("/billing/subscribe-free", { plan_id: checkout.id, cycle });
      toast.success("Plan gratuito activado");
      setCheckout(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setActivatingFree(false);
    }
  };

  const toggleRenew = async (v) => {
    try {
      await localApi.post("/billing/auto-renew", { auto_renew: v });
      toast.success(v ? "Renovación automática activada" : "Renovación automática desactivada");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const downloadReceipt = async (p) => {
    try {
      const res = await localApi.get(`/billing/payments/${p.id}/receipt`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `recibo_${p.id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(apiError(e, "No se pudo descargar el comprobante"));
    }
  };

  if (!data) {
    return (
      <div className="flex items-center gap-3 py-16 justify-center text-muted-foreground" data-testid="sub-loading">
        <CircleNotch className="h-5 w-5 animate-spin" /> Cargando suscripción...
      </div>
    );
  }

  const sub = data.subscription || {};
  const st = SUB_STATUS[data.status] || SUB_STATUS.activa;
  const StIcon = st.icon;
  const currency = data.currency || "HNL";

  return (
    <div data-testid="mi-suscripcion">
      <div className="mb-6">
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-foreground">Mi suscripción</h1>
        <p className="text-sm text-muted-foreground mt-1">Gestiona el plan de tu empresa, pagos y comprobantes</p>
      </div>

      <div className="border border-border bg-card rounded-sm p-5 mb-6" data-testid="sub-status-card">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <p className="font-heading text-xl font-bold text-foreground" data-testid="sub-plan-name">{data.plan?.name || sub.plan_name || "Plan Gratuito"}</p>
              <Badge variant="outline" className={`rounded-sm font-normal ${st.cls}`} data-testid="sub-status-badge">
                <StIcon className="mr-1" size={12} /> {st.label}
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              Ciclo {sub.billing_cycle === "annual" ? "anual" : "mensual"}
              {sub.current_period_end ? ` · vigente hasta ${fmtDate(sub.current_period_end)}` : ""}
            </p>
          </div>
          {sub.tenant_id && (
            <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
              <Switch checked={sub.auto_renew !== false} onCheckedChange={toggleRenew} data-testid="sub-auto-renew" />
              Renovación automática
            </label>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading text-lg font-bold text-foreground">Planes disponibles</h2>
        <div className="flex gap-1 border border-border rounded-sm p-0.5 bg-card">
          {[["monthly", "Mensual"], ["annual", "Anual"]].map(([k, l]) => (
            <button key={k} onClick={() => setCycle(k)} data-testid={`cycle-${k}`}
              className={`px-3 py-1.5 text-xs rounded-sm transition-colors duration-200 ${cycle === k ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
              {l}
            </button>
          ))}
        </div>
      </div>

      {plans.length === 0 ? (
        <div className="border border-border rounded-sm p-10 text-center text-muted-foreground bg-card" data-testid="plans-empty">
          No hay planes publicados por el momento.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 mb-8" data-testid="plans-grid">
          {plans.map((p) => {
            const isCurrent = sub.plan_id === p.id;
            const price = cycle === "annual" && p.annual_price ? p.annual_price : p.monthly_price;
            return (
              <div key={p.id} className={`border rounded-sm p-5 bg-card transition-colors duration-200 ${isCurrent ? "border-primary" : "border-border hover:border-primary/50"}`} data-testid={`plan-card-${p.id}`}>
                <div className="flex items-center justify-between">
                  <p className="font-heading text-lg font-bold text-foreground">{p.name}</p>
                  {isCurrent && <Badge variant="outline" className="rounded-sm border-primary/50 text-primary font-normal">Tu plan</Badge>}
                </div>
                <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{p.description || ""}</p>
                <div className="mt-4">
                  {p.is_free ? (
                    <p className="font-heading text-2xl font-bold text-emerald-500">Gratis</p>
                  ) : (
                    <>
                      <p className="font-heading text-2xl font-bold text-foreground">{fmtMoney(price, p.currency || currency)}</p>
                      <p className="text-[11px] text-muted-foreground">{cycle === "annual" ? "por año" : "por mes"}{p.tax_included !== false ? " · impuesto incluido" : " + impuesto"}</p>
                    </>
                  )}
                </div>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {(p.features || []).slice(0, 6).map((f) => (
                    <span key={f} className="bg-primary/10 text-primary px-2 py-0.5 rounded-sm text-[11px]">{FEATURE_LABELS[f] || f}</span>
                  ))}
                  {(p.features || []).length > 6 && <span className="text-[11px] text-muted-foreground px-1 py-0.5">+{p.features.length - 6} más</span>}
                </div>
                <Button className={`${btnPrimary} w-full mt-4`} disabled={isCurrent && !p.is_free} data-testid={`plan-select-${p.id}`} onClick={() => openCheckout(p)}>
                  <CreditCard className="mr-2" size={15} />
                  {isCurrent ? (p.is_free ? "Renovar" : "Plan actual") : (p.is_free ? "Activar gratis" : "Elegir plan")}
                </Button>
              </div>
            );
          })}
        </div>
      )}

      <h2 className="font-heading text-lg font-bold text-foreground mb-3 flex items-center gap-2">
        <Receipt size={18} className="text-primary" /> Historial de pagos
      </h2>
      {data.payments.length === 0 ? (
        <div className="border border-border rounded-sm p-8 text-center text-muted-foreground bg-card" data-testid="payments-empty">
          Aún no tienes pagos registrados.
        </div>
      ) : (
        <div className="border border-border rounded-sm overflow-x-auto bg-card" data-testid="payments-table-wrap">
          <table className="w-full text-sm min-w-[720px]" data-testid="payments-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-widest text-muted-foreground border-b border-border">
                <th className="p-3 font-normal">Fecha</th>
                <th className="p-3 font-normal">Concepto</th>
                <th className="p-3 font-normal">Método</th>
                <th className="p-3 font-normal">Total</th>
                <th className="p-3 font-normal">Estado</th>
                <th className="p-3 font-normal text-right">Comprobante</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {data.payments.map((p) => (
                <tr key={p.id} className="hover:bg-muted/70 transition-colors duration-200" data-testid={`payment-row-${p.id}`}>
                  <td className="p-3 text-foreground/80 text-xs">{fmtDate(p.paid_at || p.created_at)}</td>
                  <td className="p-3 text-foreground">{p.plan_name || "—"} · {p.billing_cycle === "annual" ? "Anual" : "Mensual"}</td>
                  <td className="p-3 text-foreground/80">{{ paypal: "PayPal", transfer: "Transferencia", cash: "Efectivo" }[p.method] || p.method}</td>
                  <td className="p-3 text-foreground font-medium">{fmtMoney(p.total, p.currency)}</td>
                  <td className="p-3">
                    <Badge variant="outline" className={`rounded-sm font-normal ${p.status === "approved" ? "border-emerald-500/40 text-emerald-500" : p.status === "pending" ? "border-amber-500/40 text-amber-500" : "border-red-500/40 text-red-400"}`}>
                      {{ approved: "Aprobado", pending: "Pendiente", rejected: "Rechazado", cancelled: "Cancelado", refunded: "Reembolsado" }[p.status] || p.status}
                    </Badge>
                  </td>
                  <td className="p-3 text-right">
                    {p.status === "approved" && (
                      <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground hover:bg-accent" data-testid={`receipt-${p.id}`} onClick={() => downloadReceipt(p)}>
                        <Download className="mr-1" size={14} /> PDF
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!checkout} onOpenChange={() => setCheckout(null)}>
        <DialogContent aria-describedby={undefined} className="bg-card border-border rounded-sm sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-foreground">
              {checkout?.is_free ? `Activar ${checkout?.name}` : `Suscribirse a ${checkout?.name}`}
            </DialogTitle>
          </DialogHeader>
          {checkout && (
            <div className="space-y-4" data-testid="checkout-dialog">
              {checkout.is_free ? (
                <>
                  <p className="text-sm text-muted-foreground">Este plan no tiene costo. Se activará de inmediato para tu empresa.</p>
                  <Button onClick={subscribeFree} disabled={activatingFree} className={`${btnPrimary} w-full`} data-testid="checkout-free-confirm">
                    {activatingFree && <CircleNotch className="animate-spin mr-2" size={15} />} Activar plan gratuito
                  </Button>
                </>
              ) : (
                <>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Ticket className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={14} />
                      <Input className={`${inputCls} pl-9 font-mono uppercase`} placeholder="Código de descuento" data-testid="checkout-code"
                        value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} />
                    </div>
                    <Button variant="outline" onClick={applyCode} disabled={quoting || !code.trim()} className="rounded-sm border-input bg-transparent text-foreground/80 hover:bg-accent" data-testid="checkout-apply-code">
                      Aplicar
                    </Button>
                  </div>

                  {quoting ? (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground py-2"><CircleNotch className="animate-spin" size={15} /> Calculando total...</div>
                  ) : quote && (
                    <div className="border border-border rounded-sm divide-y divide-border text-sm" data-testid="checkout-summary">
                      <div className="flex justify-between p-2.5"><span className="text-muted-foreground">Subtotal</span><span className="text-foreground">{fmtMoney(quote.base, quote.currency)}</span></div>
                      {quote.discount > 0 && (
                        <div className="flex justify-between p-2.5"><span className="text-muted-foreground">Descuento{quote.discount_name ? ` (${quote.discount_name})` : ""}</span><span className="text-emerald-500">- {fmtMoney(quote.discount, quote.currency)}</span></div>
                      )}
                      <div className="flex justify-between p-2.5"><span className="text-muted-foreground">Impuesto{quote.tax_included ? " (incluido)" : ""}</span><span className="text-foreground">{fmtMoney(quote.tax, quote.currency)}</span></div>
                      <div className="flex justify-between p-2.5 font-bold"><span className="text-foreground">Total {cycle === "annual" ? "anual" : "mensual"}</span><span className="text-foreground" data-testid="checkout-total">{fmtMoney(quote.total, quote.currency)}</span></div>
                    </div>
                  )}

                  {quote && paypalCfg.enabled ? (
                    <>
                      {quote.currency === "HNL" && (
                        <p className="text-[11px] text-muted-foreground">PayPal procesa el cobro en dólares (USD) al tipo de cambio vigente de la plataforma.</p>
                      )}
                      <PayPalButtons clientId={paypalCfg.client_id} planId={checkout.id} cycle={cycle} code={code} onPaid={() => { setCheckout(null); load(); }} />
                    </>
                  ) : quote && !paypalCfg.enabled ? (
                    <p className="border border-amber-500/30 bg-amber-500/5 rounded-sm p-3 text-sm text-amber-500" data-testid="checkout-no-paypal">
                      Los pagos en línea no están habilitados aún. Contacta a soporte para activar tu plan por transferencia.
                    </p>
                  ) : null}
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
